import { create } from 'zustand';
import { User, AuthState } from '@/types';
import { authService, LoginCredentials, LogoutReason, RegisterData } from '@/services/auth';
import { crossTabSync } from '@/services/crossTabSync';
import toast from 'react-hot-toast';
import { notifyError } from '@/utils/errorUtils';
import { saveReturnTo } from '@/utils/returnTo';
import { readAuthToken } from '@/utils/authToken';

interface AuthActions {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: (reason?: LogoutReason) => Promise<void>;
  refreshUser: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  initialize: () => void;
  setAuthenticatedUser: (user: User) => void;
  hasInitialized: boolean;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true, // Start with loading true to prevent flash
  hasInitialized: false,

      initialize: async () => {
        // Prevent multiple initializations during the app's lifecycle
        if (get().hasInitialized && !get().isLoading) {
          return;
        }

        set({ isLoading: true });

        try {
          // HIPAA CRITICAL: Check if session was expired due to inactivity
          // If so, DO NOT auto-login via session recovery - user must re-authenticate
          const sessionExpired = sessionStorage.getItem('hipaa_session_expired');
          if (sessionExpired === 'true') {
            console.log('[AuthStore] HIPAA session expired flag detected - skipping auto-login');
            // Clear the flag (one-time use)
            sessionStorage.removeItem('hipaa_session_expired');
            // Clear any lingering auth data
            sessionStorage.removeItem('user');
            sessionStorage.removeItem('authToken');
            sessionStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('authToken');
            localStorage.removeItem('token');
            // Set unauthenticated state
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              hasInitialized: true,
            });
            return;
          }

          // HIPAA: sessionStorage is the live source. A leftover localStorage
          // authToken is migrated once by readAuthToken() and then deleted.
          const storedUser = authService.getStoredUser();
          const storedToken = readAuthToken();

          if (storedUser && storedToken) {
            // User AND token are stored in sessionStorage (from magic link), trust it
            set({
              user: storedUser,
              isAuthenticated: true,
              isLoading: false,
              hasInitialized: true,
            });
            return;
          }

          // If we have partial data, clear it
          if (storedUser || storedToken) {
            sessionStorage.removeItem('user');
            sessionStorage.removeItem('authToken');
            sessionStorage.removeItem('token'); // Clear old key too
            localStorage.removeItem('user');
            localStorage.removeItem('authToken');
            localStorage.removeItem('token'); // Clear old key too
          }

          // HIPAA: Always verify session with backend
          // The session cookie is httpOnly, so we need an API call
          try {
            const response = await authService.checkSession();
            if (response.authenticated && response.user) {
              set({
                user: response.user,
                isAuthenticated: true,
                isLoading: false,
                hasInitialized: true,
              });
              return;
            }
          } catch (error) {
            // Session check failed, continue to set unauthenticated state
          }

          // No valid auth found - set to unauthenticated
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            hasInitialized: true,
          });
        } catch (error) {
          // Handle cases where the API might be down
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            hasInitialized: true,
          });
        }
      },

      login: async (credentials: LoginCredentials) => {
        try {
          set({ isLoading: true });
          const { user } = await authService.login(credentials);

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            hasInitialized: true, // Mark as initialized after login
          });

          toast.success(`Welcome back, ${user.name}!`);
        } catch (error: unknown) {
          set({ isLoading: false });
          notifyError(error, 'Login failed');
          throw error;
        }
      },

      register: async (data: RegisterData) => {
        try {
          set({ isLoading: true });
          const { user } = await authService.register(data);

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          });

          toast.success(`Welcome, ${user.name}!`);
        } catch (error: unknown) {
          set({ isLoading: false });
          notifyError(error, 'Registration failed');
          throw error;
        }
      },

      logout: async (reason: LogoutReason = 'user') => {
        try {
          await authService.logout(reason);
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          set({
            user: null,
            isAuthenticated: false,
          });
          toast.success('Logged out successfully');
        }
      },

      refreshUser: async () => {
        try {
          const user = await authService.getCurrentUser();
          set({ user });
        } catch (error) {
          console.error('Failed to refresh user:', error);
          // If refresh fails, logout. This is the token-expiry path: the periodic
          // /auth/me refresh in useAuth is what discovers a 401 after the JWT or the
          // server session has lapsed. Nobody chose to sign out here, so it must not
          // retire the Google grant — the person is still at their desk and will be
          // signing straight back in.
          get().logout('expired');
        }
      },

      setLoading: (isLoading: boolean) => {
        set({ isLoading });
      },

      setAuthenticatedUser: (user: User) => {
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          hasInitialized: true,
        });

        // Broadcast login to other tabs (for OAuth callbacks)
        crossTabSync.broadcast('LOGIN');
      },
}));

// Subscribe to cross-tab sync events
// This ensures logout/session expiration is synchronized across all browser tabs
crossTabSync.subscribe((event) => {
  if (event === 'LOGOUT' || event === 'SESSION_EXPIRED') {
    console.log(`[AuthStore] Received ${event} from another tab - logging out`);

    // Clear local auth state
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('token'); // Clear old key too
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
    localStorage.removeItem('token'); // Clear old key too

    // Update store state
    useAuthStore.setState({
      user: null,
      isAuthenticated: false,
    });

    // Show notification to user
    const message = event === 'LOGOUT'
      ? 'You have been logged out from another tab'
      : 'Your session has expired';
    toast.error(message);

    // Involuntary expiry (not a deliberate LOGOUT): preserve the current
    // deposit search/filters/page so re-login restores it.
    if (event === 'SESSION_EXPIRED') {
      saveReturnTo();
    }

    // Redirect to login if not already there
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  } else if (event === 'LOGIN') {
    console.log('[AuthStore] Received LOGIN from another tab - refreshing user');
    // Optionally refresh user data in this tab
    useAuthStore.getState().initialize();
  }
});